import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";

const show = (v) => (Array.isArray(v) ? v.join(", ") : v === null || v === undefined || v === "" ? "—" : String(v));

export function MergeDialog({ pair, onClose }) {
  const [data, setData] = useState(null);
  const [survivor, setSurvivor] = useState("a");
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/client-master/compare/${pair.a.id}/${pair.b.id}`).then((r) => setData(r.data)); }, [pair]);
  useEffect(() => {
    if (!data) return;
    const other = survivor === "a" ? "b" : "a";
    setPick(Object.fromEntries(data.fields.map((f) => [f, show(data[survivor][f]) === "—" && show(data[other][f]) !== "—" ? other : survivor])));
  }, [data, survivor]);

  async function submit() {
    const s = data[survivor], m = data[survivor === "a" ? "b" : "a"];
    if (!window.confirm(`Merge "${m.client_name}" into "${s.client_name}"? Its tasks, compliance, invoices and Xero link move across; the merged record is archived (never deleted).`)) return;
    setBusy(true);
    try {
      const values = Object.fromEntries(data.fields.map((f) => [f, data[pick[f]][f] ?? ""]));
      await api.post("/client-master/merge", { survivor_id: s.id, merged_id: m.id, values });
      toast.success("Clients merged"); onClose(true);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); setBusy(false); }
  }

  return (
    <>
      <div className="backdrop" onClick={() => onClose(false)} />
      <div className="drawer !max-w-3xl" data-testid="merge-dialog">
        <div className="p-8">
          <div className="overline mb-2">Merge duplicates</div>
          <h2 className="font-display text-3xl mb-4">Merge clients.</h2>
          {!data ? <div className="text-sm text-[var(--ink-2)]">Loading…</div> : (
            <>
              <div className="label-overline mb-2">Surviving record</div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {["a", "b"].map((k) => (
                  <label key={k} className={`card-flat p-3 cursor-pointer text-sm ${survivor === k ? "border-[var(--navy)] bg-[var(--navy-soft)]" : ""}`}>
                    <input type="radio" className="mr-2 accent-[var(--navy)]" data-testid={`merge-survivor-${k}`} checked={survivor === k} onChange={() => setSurvivor(k)} />
                    <b>{data[k].client_name}</b>
                    <div className="text-[10px] font-mono-data text-[var(--ink-2)] mt-1">{data[k].client_uid || "No ID"} · created {(data[k].created_at || "").slice(0, 10) || "—"}</div>
                  </label>
                ))}
              </div>
              <div className="label-overline mb-2">Choose values where the records differ ({data.fields.length})</div>
              <table className="tbl tbl-ultra mb-5" data-testid="merge-fields">
                <thead><tr><th>Field</th><th>{data.a.client_name}</th><th>{data.b.client_name}</th></tr></thead>
                <tbody>
                  {data.fields.map((f) => (
                    <tr key={f}>
                      <td className="text-xs font-mono-data">{f}</td>
                      {["a", "b"].map((k) => (
                        <td key={k} className="text-xs">
                          <label className="flex items-start gap-1.5 cursor-pointer">
                            <input type="radio" className="mt-0.5 accent-[var(--navy)]" data-testid={`merge-pick-${f}-${k}`} checked={pick[f] === k} onChange={() => setPick({ ...pick, [f]: k })} />
                            <span className="break-words max-w-[260px]">{show(data[k][f])}</span>
                          </label>
                        </td>
                      ))}
                    </tr>
                  ))}
                  {!data.fields.length && <tr><td colSpan={3} className="text-xs text-[var(--ink-2)]">The two records hold identical details.</td></tr>}
                </tbody>
              </table>
              <div className="flex gap-2">
                <button data-testid="merge-confirm" disabled={busy} onClick={submit} className="btn-primary">{busy ? "Merging…" : "Merge clients"}</button>
                <button onClick={() => onClose(false)} className="btn-ghost">Cancel</button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
